import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Square, FileText, User, Send, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import Navbar from './home/Navbar';
import Footer from './Footer';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';

// Status colors adapted to brand
const COLORS = {
  new: { bg: 'bg-yellow-400', text: 'text-yellow-900', border: 'border-yellow-400' },
  open: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-600' },
  pending: { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-600' },
  on_hold: { bg: 'bg-gray-900', text: 'text-white', border: 'border-gray-900' },
  solved: { bg: 'bg-gray-400', text: 'text-white', border: 'border-gray-400' },
};

// Event type colors
const EVENT_COLORS = {
  customer_email: 'bg-[#1c3861]',
  customer_reply: 'bg-[#1c3861]',
  internal_note: 'bg-yellow-500',
  agent_reply: 'bg-[#ffa100]',
  status_change: 'bg-gray-800',
  solve: 'bg-gray-500',
};

function SlaSimulator() {
  const { t } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();
  const [status, setStatus] = useState('new');
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState({
    firstReplyTime: null,
    requesterWaitTime: 0,
    agentWorkTime: 0,
    resolutionTime: 0,
  });
  
  // Target configuration state
  const [showTargetConfig, setShowTargetConfig] = useState(true);
  const [targetsLocked, setTargetsLocked] = useState(false);
  const [targets, setTargets] = useState({
    firstReplyTime: { hours: 0, minutes: 0 },
    requesterWaitTime: { hours: 0, minutes: 0 },
    agentWorkTime: { hours: 0, minutes: 0 },
    resolutionTime: { hours: 0, minutes: 0 },
  });

  const lastTickRef = useRef(null);
  const timelineEndRef = useRef(null);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-scroll timeline to bottom when new event is added
  useEffect(() => {
    if (timelineEndRef.current) {
      timelineEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [events]);

  // Timer Loop
  useEffect(() => {
    let interval;
    if (isRunning && status !== 'solved') {
      lastTickRef.current = Date.now();
      interval = setInterval(() => {
        const now = Date.now();
        const delta = (now - lastTickRef.current) / 1000;
        lastTickRef.current = now;

        setElapsedTime(prev => prev + delta);
        updateMetrics(delta, status);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isRunning, status]);

  const updateMetrics = (delta, currentStatus) => {
    setMetrics(prev => {
      const newMetrics = { ...prev };
      
      newMetrics.resolutionTime += delta;

      if (['new', 'open', 'on_hold'].includes(currentStatus)) {
        newMetrics.requesterWaitTime += delta;
      }

      if (['new', 'open'].includes(currentStatus)) {
        newMetrics.agentWorkTime += delta;
      }

      return newMetrics;
    });
  };

  const addEvent = (type, label, nextStatus) => {
    const newEvent = {
      id: Date.now(),
      time: elapsedTime,
      type,
      label,
      prevStatus: status,
      nextStatus: nextStatus || status,
    };

    setEvents([...events, newEvent]);
    
    // Track event in Google Analytics
    gaEvents.addSlaEvent(type, nextStatus || status, elapsedTime);
    
    if (type === 'agent_reply' && metrics.firstReplyTime === null) {
      setMetrics(prev => ({ ...prev, firstReplyTime: elapsedTime }));
    }

    if (nextStatus) {
      setStatus(nextStatus);
    }
    
    if (nextStatus === 'solved') {
      setIsRunning(false);
      
      // Track simulation completion
      const achievementStats = calculateAchievementStats();
      gaEvents.completeSlaSimulation(
        {
          ...metrics,
          firstReplyTime: type === 'agent_reply' && metrics.firstReplyTime === null ? elapsedTime : metrics.firstReplyTime
        },
        hasAnyTargets(),
        achievementStats
      );
    } else if (!isRunning && events.length > 0) {
      setIsRunning(true);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleReset = () => {
    // Track reset event
    gaEvents.resetSlaSimulation();
    
    setIsRunning(false);
    setElapsedTime(0);
    setStatus('new');
    setEvents([]);
    setMetrics({
      firstReplyTime: null,
      requesterWaitTime: 0,
      agentWorkTime: 0,
      resolutionTime: 0,
    });
    setShowTargetConfig(true);
    setTargetsLocked(false);
    setTargets({
      firstReplyTime: { hours: 0, minutes: 0 },
      requesterWaitTime: { hours: 0, minutes: 0 },
      agentWorkTime: { hours: 0, minutes: 0 },
      resolutionTime: { hours: 0, minutes: 0 },
    });
  };

  const handleStartSimulation = () => {
    // Track simulation start with targets info
    gaEvents.startSlaSimulation(
      hasAnyTargets(),
      {
        firstReplyTime: convertTargetToSeconds(targets.firstReplyTime),
        requesterWaitTime: convertTargetToSeconds(targets.requesterWaitTime),
        agentWorkTime: convertTargetToSeconds(targets.agentWorkTime),
        resolutionTime: convertTargetToSeconds(targets.resolutionTime)
      }
    );
    
    setShowTargetConfig(false);
    setTargetsLocked(true);
    // Add initial customer email event
    addEvent('customer_email', t('sla_simulator.events.customer_email'), 'new');
    setIsRunning(true);
  };

  const convertTargetToSeconds = (target) => {
    return (target.hours * 3600) + (target.minutes * 60);
  };

  const hasAnyTargets = () => {
    return Object.values(targets).some(target => target.hours > 0 || target.minutes > 0);
  };

  const calculateAchievementStats = () => {
    if (!hasAnyTargets()) return { totalAchieved: 0, totalBreached: 0 };
    
    let totalAchieved = 0;
    let totalBreached = 0;
    
    // Check each metric with a target
    if (convertTargetToSeconds(targets.firstReplyTime) > 0 && metrics.firstReplyTime !== null) {
      const achievement = calculateAchievement(metrics.firstReplyTime, targets.firstReplyTime);
      if (achievement?.achieved) totalAchieved++; else totalBreached++;
    }
    
    if (convertTargetToSeconds(targets.requesterWaitTime) > 0) {
      const achievement = calculateAchievement(metrics.requesterWaitTime, targets.requesterWaitTime);
      if (achievement?.achieved) totalAchieved++; else totalBreached++;
    }
    
    if (convertTargetToSeconds(targets.agentWorkTime) > 0) {
      const achievement = calculateAchievement(metrics.agentWorkTime, targets.agentWorkTime);
      if (achievement?.achieved) totalAchieved++; else totalBreached++;
    }
    
    if (convertTargetToSeconds(targets.resolutionTime) > 0) {
      const achievement = calculateAchievement(metrics.resolutionTime, targets.resolutionTime);
      if (achievement?.achieved) totalAchieved++; else totalBreached++;
    }
    
    return { totalAchieved, totalBreached };
  };

  const calculateAchievement = (actual, targetObj) => {
    const targetSeconds = convertTargetToSeconds(targetObj);
    if (targetSeconds === 0) return null;
    
    const achieved = actual <= targetSeconds;
    const percentage = ((actual / targetSeconds) * 100).toFixed(1);
    
    return {
      achieved,
      percentage: parseFloat(percentage),
      difference: actual - targetSeconds
    };
  };

  const getProgressColor = (actual, targetObj) => {
    const targetSeconds = convertTargetToSeconds(targetObj);
    if (targetSeconds === 0) return 'from-blue-500 to-blue-600';
    
    const ratio = actual / targetSeconds;
    if (ratio < 0.7) return 'from-green-500 to-green-600';
    if (ratio < 0.9) return 'from-yellow-500 to-yellow-600';
    if (ratio < 1.0) return 'from-orange-500 to-orange-600';
    return 'from-red-500 to-red-600';
  };

  const updateTargetValue = (metricKey, field, value) => {
    const numValue = parseInt(value) || 0;
    setTargets(prev => ({
      ...prev,
      [metricKey]: {
        ...prev[metricKey],
        [field]: numValue
      }
    }));
  };

  const isMetricActive = (metricType) => {
    if (!isRunning || status === 'solved') return false;
    
    switch(metricType) {
      case 'requesterWait':
        return ['new', 'open', 'on_hold'].includes(status);
      case 'agentWork':
        return ['new', 'open'].includes(status);
      case 'resolution':
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ink font-sans text-white antialiased">
      <Navbar />
      <div className="h-16" />
      
      <div className="bg-ink font-sans text-white flex flex-col py-8">
        <main className="flex-1 p-4 sm:p-6 max-w-7xl mx-auto w-full">
          
          {/* Page Title */}
          <div className="bg-surface p-4 sm:p-6 rounded-lg border border-line mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cta rounded flex items-center justify-center font-bold text-xl text-ink">S</div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('sla_simulator.title')}</h1>
                <p className="text-sm text-white/50 mt-1">{t('sla_simulator.metrics_subtitle')}</p>
              </div>
            </div>
          </div>

          {/* Target Configuration Section */}
          {showTargetConfig && !targetsLocked && (
            <div className="bg-surface rounded-lg border border-line mb-6 overflow-hidden
              [&_input]:bg-ink [&_input]:border-line [&_input]:text-white
              [&_label]:text-white/70">
              <div className="bg-elevated border-b border-line text-white px-4 py-2">
                <h2 className="font-bold text-base flex items-center gap-2">
                  <AlertCircle size={18} />
                  Configure SLA Targets (Optional)
                </h2>
                <p className="text-xs text-white/50 mt-0.5">
                  Set your target times to measure service level achievement
                </p>
              </div>
              
              <div className="p-4">
                {/* All metrics in one row */}
                <div className="flex flex-wrap gap-3 mb-3">
                  {/* First Reply Time Target */}
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      {t('sla_simulator.metrics.first_reply_time.label')}
                    </label>
                    <div className="flex gap-2">
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={targets.firstReplyTime.hours}
                          onChange={(e) => updateTargetValue('firstReplyTime', 'hours', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.hours')}</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={targets.firstReplyTime.minutes}
                          onChange={(e) => updateTargetValue('firstReplyTime', 'minutes', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.minutes')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Requester Wait Time Target */}
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      {t('sla_simulator.metrics.requester_wait_time.label')}
                    </label>
                    <div className="flex gap-2">
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={targets.requesterWaitTime.hours}
                          onChange={(e) => updateTargetValue('requesterWaitTime', 'hours', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.hours')}</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={targets.requesterWaitTime.minutes}
                          onChange={(e) => updateTargetValue('requesterWaitTime', 'minutes', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.minutes')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Agent Work Time Target */}
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      {t('sla_simulator.metrics.agent_work_time.label')}
                    </label>
                    <div className="flex gap-2">
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={targets.agentWorkTime.hours}
                          onChange={(e) => updateTargetValue('agentWorkTime', 'hours', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.hours')}</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={targets.agentWorkTime.minutes}
                          onChange={(e) => updateTargetValue('agentWorkTime', 'minutes', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.minutes')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Resolution Time Target */}
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      {t('sla_simulator.metrics.total_resolution_time.label')}
                    </label>
                    <div className="flex gap-2">
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={targets.resolutionTime.hours}
                          onChange={(e) => updateTargetValue('resolutionTime', 'hours', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.hours')}</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={targets.resolutionTime.minutes}
                          onChange={(e) => updateTargetValue('resolutionTime', 'minutes', e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffa100] focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 block text-center mt-0.5">{t('sla_simulator.time_units.minutes')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  {!hasAnyTargets() && (
                    <p className="text-xs text-white/50 italic">
                      💡 No targets set - running in free mode
                    </p>
                  )}
                  {hasAnyTargets() && (
                    <p className="text-xs text-green-400 font-medium">
                      ✓ Targets configured - SLA achievement will be calculated
                    </p>
                  )}
                  <button
                    onClick={handleStartSimulation}
                    className="bg-cta hover:bg-cta-hover text-ink font-bold py-2 px-4 rounded-lg shadow-md transition-all transform hover:-translate-y-0.5 text-sm"
                  >
                    Start Simulation
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Two-Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT PANEL: Vertical Timeline (Clinical History / Chat Log) */}
            <div className="bg-surface rounded-lg border border-line overflow-hidden">
              <div className="bg-elevated border-b border-line text-white p-4 flex items-center gap-2">
                <Clock size={20} />
                <h2 className="font-bold text-lg">{t('sla_simulator.event_flow')}</h2>
              </div>
              
              {/* Timeline Container with Scroll */}
              <div className="p-6 max-h-[600px] overflow-y-auto">
                <div className="relative">
                  {/* Vertical connecting line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-accent"></div>
                  
                  {/* Events */}
                  <div className="space-y-4">
                    {events.map((evt, idx) => (
                      <div key={evt.id} className="relative pl-12 animate-in fade-in slide-in-from-left duration-500">
                        {/* Timeline dot */}
                        <div className="absolute left-2.5 top-2 w-3 h-3 rounded-full bg-accent border-2 border-ink shadow-md z-10"></div>
                        
                        {/* Event card */}
                        <div className="bg-elevated rounded-lg p-3 border border-line hover:border-accent/40 transition-colors">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded ${EVENT_COLORS[evt.type] || 'bg-gray-500'} text-white`}>
                                {evt.type === 'customer_email' || evt.type === 'customer_reply' ? <User size={14} /> :
                                 evt.type === 'internal_note' ? <FileText size={14} /> :
                                 evt.type === 'agent_reply' ? <Send size={14} /> :
                                 evt.type === 'solve' ? <CheckCircle size={14} /> :
                                 <Clock size={14} />}
                              </div>
                              <span className="text-xs font-mono text-white/50">{formatTime(evt.time)}</span>
                            </div>
                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${COLORS[evt.nextStatus].bg} ${COLORS[evt.nextStatus].text}`}>
                              {t(`sla_simulator.statuses.${evt.nextStatus}`)}
                            </span>
                          </div>
                          <p className="text-sm text-white/80 font-medium">{evt.label}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={timelineEndRef} />
                  </div>
                </div>
              </div>
              
              {/* Legend at bottom */}
              <div className="bg-elevated p-3 border-t border-line">
                <div className="text-xs text-white/60 space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#1c3861] rounded"></div>
                    <span>{t('sla_simulator.legend.active_time')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-300 rounded"></div>
                    <span>{t('sla_simulator.legend.paused_time')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT PANEL: Control Dashboard + Metrics */}
            <div className="space-y-6">
              
              {/* Control Panel */}
              <div className="bg-surface rounded-lg border border-line p-6">
                {/* Timer and Controls */}
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-line">
                  <div className="flex flex-col">
                    <span className="text-xs text-white/50 uppercase tracking-wide mb-1">{t('sla_simulator.elapsed_time')}</span>
                    <span className="text-4xl font-mono font-bold text-white">{formatTime(elapsedTime)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        const newIsRunning = !isRunning;
                        // Track pause/resume
                        if (newIsRunning) {
                          gaEvents.resumeSlaSimulation();
                        } else {
                          gaEvents.pauseSlaSimulation();
                        }
                        setIsRunning(newIsRunning);
                      }}
                      type="button"
                      disabled={status === 'solved'}
                      className={`p-3 rounded-lg ${isRunning ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-[#ffa100] hover:bg-[#ff9100]'} transition-colors text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={isRunning ? 'Pause' : 'Play'}
                    >
                      {isRunning ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <button 
                      onClick={handleReset}
                      type="button"
                      className="p-3 rounded-lg bg-gray-600 hover:bg-gray-700 text-white shadow-md transition-colors"
                      title="Reset"
                    >
                      <Square size={20} fill="currentColor" />
                    </button>
                  </div>
                </div>

                {/* Current Status */}
                <div className="mb-6">
                  <h3 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2">{t('sla_simulator.ticket_status')}</h3>
                  <div className="flex items-center gap-3">
                    <span className={`px-4 py-2 rounded-lg text-base font-bold shadow-md ${COLORS[status].bg} ${COLORS[status].text}`}>
                      {t(`sla_simulator.statuses.${status}`)}
                    </span>
                    <span className="text-xs text-white/40 italic">
                      ({t('sla_simulator.clock_info.' + (status === 'pending' || status === 'solved' ? 'paused' : 'running'))})
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                {status !== 'solved' ? (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-3">Actions</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <ActionButton 
                        icon={<FileText size={16}/>} 
                        label={t('sla_simulator.actions.internal_note')} 
                        color="bg-yellow-500 hover:bg-yellow-600" 
                        onClick={() => addEvent('internal_note', t('sla_simulator.events.internal_note'), 'open')}
                      />
                      <ActionButton 
                        icon={<Send size={16}/>} 
                        label={t('sla_simulator.actions.agent_reply')} 
                        color="bg-[#ffa100] hover:bg-[#ff9100]" 
                        onClick={() => addEvent('agent_reply', t('sla_simulator.events.agent_reply'), 'pending')}
                      />
                      <ActionButton 
                        icon={<User size={16}/>} 
                        label={t('sla_simulator.actions.customer_reply')} 
                        color="bg-[#1c3861] hover:bg-[#0f2645]" 
                        onClick={() => addEvent('customer_reply', t('sla_simulator.events.customer_reply'), 'open')}
                      />
                      <ActionButton 
                        icon={<Clock size={16}/>} 
                        label={t('sla_simulator.actions.on_hold')} 
                        color="bg-gray-800 hover:bg-gray-900" 
                        onClick={() => addEvent('status_change', t('sla_simulator.events.on_hold'), 'on_hold')}
                      />
                      <ActionButton 
                        icon={<CheckCircle size={16}/>} 
                        label={t('sla_simulator.actions.solve')} 
                        color="bg-gray-500 hover:bg-gray-600" 
                        onClick={() => addEvent('solve', t('sla_simulator.events.solve'), 'solved')}
                        fullWidth
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <CheckCircle size={32} className="text-green-600 mx-auto mb-2" />
                      <h4 className="text-green-800 text-center font-bold text-lg mb-1">{t('sla_simulator.completed.title')}</h4>
                      <p className="text-sm text-green-700 mb-3">
                        {t('sla_simulator.completed.message', {
                          resolution_time: formatTime(metrics.resolutionTime),
                          wait_time: formatTime(metrics.requesterWaitTime)
                        })}
                      </p>
                    </div>

                    {/* SLA Achievement Results */}
                    {hasAnyTargets() && (
                      <div className="bg-surface border border-line rounded-lg p-4">
                        <h5 className="font-bold text-sm text-white/80 mb-3 flex items-center gap-2">
                          <AlertCircle size={16} />
                          SLA Achievement Results
                        </h5>
                        <div className="space-y-2">
                          {/* First Reply Time */}
                          {convertTargetToSeconds(targets.firstReplyTime) > 0 && metrics.firstReplyTime !== null && (
                            <AchievementRow
                              label={t('sla_simulator.metrics.first_reply_time.label')}
                              achievement={calculateAchievement(metrics.firstReplyTime, targets.firstReplyTime)}
                            />
                          )}
                          
                          {/* Requester Wait Time */}
                          {convertTargetToSeconds(targets.requesterWaitTime) > 0 && (
                            <AchievementRow
                              label={t('sla_simulator.metrics.requester_wait_time.label')}
                              achievement={calculateAchievement(metrics.requesterWaitTime, targets.requesterWaitTime)}
                            />
                          )}
                          
                          {/* Agent Work Time */}
                          {convertTargetToSeconds(targets.agentWorkTime) > 0 && (
                            <AchievementRow
                              label={t('sla_simulator.metrics.agent_work_time.label')}
                              achievement={calculateAchievement(metrics.agentWorkTime, targets.agentWorkTime)}
                            />
                          )}
                          
                          {/* Total Resolution Time */}
                          {convertTargetToSeconds(targets.resolutionTime) > 0 && (
                            <AchievementRow
                              label={t('sla_simulator.metrics.total_resolution_time.label')}
                              achievement={calculateAchievement(metrics.resolutionTime, targets.resolutionTime)}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Metrics Panel */}
              <div className="bg-surface rounded-lg border border-line overflow-hidden">
                <div className="bg-elevated border-b border-line text-white p-4 flex items-center gap-2">
                  <AlertCircle size={20} />
                  <h3 className="font-bold text-lg">{t('sla_simulator.metrics_title')}</h3>
                </div>

                <div className="divide-y divide-line">
                  <MetricCard 
                    label={t('sla_simulator.metrics.first_reply_time.label')}
                    value={formatTime(metrics.firstReplyTime)} 
                    description={t('sla_simulator.metrics.first_reply_time.description')}
                    active={false}
                    color="text-blue-600"
                    showProgress={false}
                  />
                  
                  <MetricCard 
                    label={t('sla_simulator.metrics.requester_wait_time.label')}
                    value={formatTime(metrics.requesterWaitTime)} 
                    description={t('sla_simulator.metrics.requester_wait_time.description')}
                    active={isMetricActive('requesterWait')}
                    color="text-red-600"
                    showProgress={true}
                    progressPercent={isMetricActive('requesterWait') ? 100 : 0}
                    progressColor={getProgressColor(metrics.requesterWaitTime, targets.requesterWaitTime)}
                  />

                  <MetricCard 
                    label={t('sla_simulator.metrics.agent_work_time.label')}
                    value={formatTime(metrics.agentWorkTime)} 
                    description={t('sla_simulator.metrics.agent_work_time.description')}
                    active={isMetricActive('agentWork')}
                    color="text-[#ffa100]"
                    showProgress={true}
                    progressPercent={isMetricActive('agentWork') ? 100 : 0}
                    progressColor={getProgressColor(metrics.agentWorkTime, targets.agentWorkTime)}
                  />

                  <MetricCard 
                    label={t('sla_simulator.metrics.total_resolution_time.label')}
                    value={formatTime(metrics.resolutionTime)} 
                    description={t('sla_simulator.metrics.total_resolution_time.description')}
                    active={isMetricActive('resolution')}
                    color="text-white"
                    showProgress={false}
                    highlight
                  />
                </div>
              </div>
            </div>
            
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}

function ActionButton({ icon, label, color, onClick, fullWidth }) {
  const handleClick = (e) => {
    e.preventDefault();
    onClick();
  };
  
  return (
    <button 
      onClick={handleClick}
      type="button"
      className={`${color} ${fullWidth ? 'col-span-2' : ''} text-white px-3 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all transform hover:-translate-y-0.5`}
    >
      {icon} <span>{label}</span>
    </button>
  );
}

function MetricCard({ label, value, description, active, color, showProgress, progressPercent, progressColor, highlight }) {
  const { t } = useTranslation();
  
  return (
    <div className={`p-4 transition-colors ${active ? 'bg-accent/10' : 'bg-surface'} ${highlight ? 'border-l-4 border-l-cta' : ''}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <span className="text-sm font-bold text-white/80 block mb-1">{label}</span>
          <p className="text-xs text-white/50 leading-tight">{description}</p>
        </div>
        <span className={`font-mono text-2xl font-bold ${color} ml-4 ${active ? 'animate-pulse' : ''}`}>
          {value}
        </span>
      </div>
      
      {active && (
        <div className="text-[10px] uppercase font-bold text-accent-400 mb-2 flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-ping"></div>
          {t('sla_simulator.counting')}
        </div>
      )}
      
      {showProgress && (
        <div className="mt-2">
          <div className="h-1.5 bg-line rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${active ? `bg-gradient-to-r ${progressColor}` : 'bg-white/20'} ${active ? 'animate-pulse' : ''}`}
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
}

function AchievementRow({ label, achievement }) {
  if (!achievement) return null;
  
  const { achieved } = achievement;
  const displayText = achieved ? 'Achieved' : 'Breached';
  
  return (
    <div className={`flex items-center justify-between p-2 rounded ${achieved ? 'bg-green-50' : 'bg-red-50'}`}>
      <div className="flex items-center gap-2">
        {achieved ? (
          <CheckCircle size={16} className="text-green-600" />
        ) : (
          <AlertCircle size={16} className="text-red-600" />
        )}
        <span className="text-xs font-medium text-gray-700">{label}</span>
      </div>
      <span className={`text-xs font-bold ${achieved ? 'text-green-700' : 'text-red-700'}`}>
        {displayText}
      </span>
    </div>
  );
}

export default SlaSimulator;
